import { Trash } from 'lucide-react';
import { useState } from 'react';
import {
  type RedirectionSideEffect,
  useDeleteController,
  useRecordContext,
  useResourceContext,
  useTranslate,
} from 'ra-core';
import { Confirm } from '../../components/admin/confirm';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

type MoonstarDeleteButtonProps = {
  label?: string;
  redirect?: RedirectionSideEffect;
  resource?: string;
  successMessage?: string;
  className?: string;
};

/**
 * Deletes the current record after a confirmation dialog, waiting for the server (pessimistic).
 * The kit `DeleteButton` deletes straight away with undo instead.
 */
export const MoonstarDeleteButton = ({
  label = 'ra.action.delete',
  redirect = 'list',
  successMessage,
  className,
  ...props
}: MoonstarDeleteButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const record = useRecordContext();
  const resource = useResourceContext(props);
  const translate = useTranslate();

  const { handleDelete, isPending } = useDeleteController({
    record,
    resource,
    redirect,
    successMessage,
    mutationMode: 'pessimistic',
    mutationOptions: {
      onSettled: () => setIsOpen(false),
    },
  });

  if (!record) {
    return null;
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className={cn(
          'border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive',
          className
        )}
        disabled={isPending}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(true);
        }}
      >
        <Trash />
        {translate(label, { _: label })}
      </Button>
      <Confirm
        isOpen={isOpen}
        loading={isPending}
        title="ra.message.delete_title"
        content="ra.message.delete_content"
        confirmColor="warning"
        onConfirm={handleDelete}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};
